import { Model } from 'pinia-orm';

export class Variation extends Model {
  static entity = 'variations';
  static primaryKey = 'id';

  static fields() {
    return {
      id: this.uid(),
      experimentId: this.string(''),
      name: this.string(''),
      weight: this.number(0),
      isControl: this.boolean(false),
      createdAt: this.attr(null),
      updatedAt: this.attr(null),
    };
  }

  declare id: string | null;
  declare experimentId: string;
  declare name: string;
  declare weight: number;
  declare isControl: boolean;
  declare createdAt: string | null;
  declare updatedAt: string | null;

  // Track original values for change detection
  private _originalValues: Record<string, any> = {};

  static hydrate(data: any) {
    const variation = new this(data);
    variation._setOriginalValues();
    return variation;
  }

  _setOriginalValues() {
    this._originalValues = {
      name: this.name,
      weight: this.weight,
      isControl: this.isControl,
    };
  }

  get hasChanges(): boolean {
    return Object.keys(this._originalValues).some(
      (key) => this._originalValues[key] !== (this as any)[key]
    );
  }

  get isValid(): boolean {
    return this.name.trim().length > 0 && this.weight >= 0 && this.weight <= 100;
  }

  static fromAPI(apiData: any): Variation {
    return this.hydrate({
      id: apiData.id,
      experimentId: apiData.experiment_id || apiData.experimentId,
      name: apiData.name || '',
      weight: apiData.weight || 0,
      isControl: apiData.is_control || apiData.isControl || false,
      createdAt: apiData.created_at || apiData.createdAt,
      updatedAt: apiData.updated_at || apiData.updatedAt,
    });
  }

  toAPI(): any {
    return {
      id: this.id,
      experiment_id: this.experimentId,
      name: this.name,
      weight: this.weight,
      is_control: this.isControl,
    };
  }

  reset() {
    Object.keys(this._originalValues).forEach((key) => {
      (this as any)[key] = this._originalValues[key];
    });
  }

  updateOriginalValues() {
    this._setOriginalValues();
  }

  // Generate next variation name
  static generateNextName(existingVariations: Variation[]): string {
    const maxNumber = existingVariations
      .map((v) => v.name)
      .filter((name) => name.startsWith('Variation '))
      .map((name) => parseInt(name.replace('Variation ', '')) || 0)
      .reduce((max, num) => Math.max(max, num), 0);

    return `Variation ${maxNumber + 1}`;
  }
}
